/**
 * Core RTK Query API slice for TIC.
 * Endpoints are injected into this slice via TIC OpenAPI -> RTK Query codegen.
 * See `openapi-tic-config.json` for config.
 * Generate using `yarn built-rtk-query`
 */

import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import type { Memory, SyncTimelyToTicResponse } from '@timelyapp/integration-cloud-api-spec';
import {
  getTicApiBaseUrl,
  getTicApiCredentialsSetting,
  getTicApiHeaders,
  getTimelySessionUrlForTicJwtExchange,
  ticQueryParamSerializer,
} from '@timelyapp/integration-cloud-sdk/api';
import { getEnvironment } from '@timelyapp/integration-cloud-sdk/api/api.environment.utils';
import { timelyEntryToTicMemory } from '@timelyapp/integration-cloud-sdk/memory-processors/timely.legacy.transformers';
import api from 'api';
import { customFetchish } from 'api/core/base';
import { Mutex } from 'async-mutex';
import { setTicAuthState } from 'redux/modules/tic/tic.auth';
import { changeTicServiceOnlineState } from 'redux/modules/tic/tic.global.actions';
import { showToast } from 'redux/modules/toasts.actions';
import type { Entry, SuggestedEntry } from 'types/timeline';
import debug from 'utils/debug';

/**
 * Prepare the base query function with TIC-specific settings.
 */
const baseQuery = fetchBaseQuery({
  baseUrl: getTicApiBaseUrl(),
  paramsSerializer: query => {
    return ticQueryParamSerializer(query);
  },
  prepareHeaders: headers => {
    // Use TIC SDK to determine which headers we need to inject.
    const ticHeaders = getTicApiHeaders();

    // Inject them into the request.
    Object.keys(ticHeaders).forEach(key => {
      headers.set(key, ticHeaders[key]);
    });

    return headers;
  },
  // In prod, credentials: include is used.
  credentials: getTicApiCredentialsSetting(),
});

/** Used to prevent duplicate re-auth requests below  */
const mutex = new Mutex();

/**
 * Extend base query with one-at-a-time re-authentication logic.
 * @link https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-re-authorization-by-extending-fetchbasequery
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  // Determine URL we're hitting up.
  const url = typeof args === 'object' && 'url' in args ? args.url : 'unknown';

  // Run request, as per normal.
  let result = await baseQuery(args, api, extraOptions);

  /* Handle 503/TIC maintenance mode explicitly. */
  if (result?.error?.status === 503) {
    api.dispatch(changeTicServiceOnlineState(false));
  }

  /**
   * Oh no, rate limit hit!
   * @warning Only a short 5s wait period is allowed, else we'll abort the request.
   */
  if (result?.error && result?.error.status === 429) {
    // Figure out rate limit situation
    const remaining = result.meta?.response?.headers.get('x-ratelimit-remaining');
    const reset = result.meta?.response?.headers.get('x-ratelimit-reset');
    const limit = result.meta?.response?.headers.get('x-ratelimit-limit');
    const resetsInMs = reset ? +reset - Date.now() : 0;
    const secsToReset = Math.round(resetsInMs / 1000);
    const DUMP = { args, remaining, reset, limit, resetsInMs };
    const msgPrefix = `${url} (${remaining} remaining/${limit} limit)`;

    if (getEnvironment() !== 'production') {
      api.dispatch(
        showToast({ title: 'Dev & staging debug', message: 'Rate limited', type: 'danger' })
      );
    }

    // Make sure we don't get fancy if we don't understand the rate limit headers.
    if (!remaining || !reset || !limit) {
      debug.template.rateLimit(`${msgPrefix}, rate limit headers not understood, aborting..`, DUMP);
      api.abort();
    }

    // If it's more than a few seconds until rate limit is lifted, abort the request.
    // (since things tend to change -- e.g, delayed inference often means user has navigated to different day)
    if (secsToReset > 5) {
      api.abort();
      debug.template.rateLimit(`${msgPrefix}, NOT waiting ${secsToReset}s, aborting..`, DUMP);
    } else {
      debug.template.rateLimit(`${msgPrefix}, retry in ${secsToReset}s..`, DUMP);
      await new Promise(resolve => setTimeout(resolve, resetsInMs));
      try {
        result = await baseQuery(args, api, extraOptions);
      } catch (error) {
        debug.template.rateLimit(`${msgPrefix}, retry failed..`, DUMP);
      }
    }
  }

  // In case of 401's
  if (result?.error && result?.error.status === 401) {
    // Flag the user as not authenticated (which will block future TIC API calls until re-authenticated)
    // TODO: Re-consider this when all TIC calls are via RTKQ.
    api.dispatch(setTicAuthState({ isAuthenticated: false }));

    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        // Call /tic/sessions to refresh the cookie.
        debug.template.auth('baseQueryWithReauth: Re-establishing TIC session after 401', { args });
        const promise = api.dispatch(ticCreateApiBase.endpoints.getTicSession.initiate(undefined));
        const data = await promise.unwrap();

        const DUMP = { data, endpoint: api?.endpoint, args };

        // Tell Redux we're authenticated again.
        if (data) {
          debug.template.auth('baseQueryWithReauth: Re-authenticated, will retry operation', DUMP);
          result = await baseQuery(args, api, extraOptions);
        } else {
          debug.template.auth('baseQueryWithReauth: Re-auth failed, unable to retry', DUMP);
        }
      } catch (error) {
        debug.template.auth('baseQueryWithReauth: Refresh cookie/TIC session failed.', { error });
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      // Re-run the query that failed with 401.
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

// maxRetries: 5 is the default
const staggeredBaseQuery = retry(baseQueryWithReauth, { maxRetries: 2 });

// initialize an empty api service that we'll inject endpoints into later as needed
export const ticCreateApiBase = createApi({
  reducerPath: 'ticApi',
  baseQuery: staggeredBaseQuery,
  tagTypes: ['ticSession', 'timelyLegacyMemories'],
  endpoints: build => ({
    /**
     * Timely's `/tic/sessions` endpoint refreshes `tic-sessions` httpOnly cookie & returns JWT
     */
    getTicSession: build.query<SyncTimelyToTicResponse, undefined>({
      providesTags: ['ticSession'],
      keepUnusedDataFor: Infinity,
      queryFn: async (arg, { getState }) => {
        try {
          const jwtApiEndpoint = getTimelySessionUrlForTicJwtExchange();
          if (!jwtApiEndpoint) throw new Error('No JWT API endpoint');

          const { id } = (getState() as { accounts: { current: { id: number } } }).accounts.current;
          const url = `${jwtApiEndpoint}?account_id=${id}`;
          const data = await customFetchish<SyncTimelyToTicResponse>(url, {});

          debug.template.auth('Synced user & accounts via /tic/sessions', data);
          return { data };
        } catch (error) {
          debug.template.auth('Failed to sync Timely user & accounts with TIC', { error });
          return { error };
        }
      },
      /** Set TIC authenticated state separately */
      onCacheEntryAdded: async (arg, { dispatch, cacheDataLoaded }) => {
        try {
          await cacheDataLoaded;
          dispatch(setTicAuthState({ isAuthenticated: true }));
        } catch (error) {
          dispatch(setTicAuthState({ isAuthenticated: false }));
        }
      },
    }),
    /**
     * Timely's `/suggested_entries` endpoint, needed to play nice with TIC Memories on RTKQ.
     */
    getTimelyLegacyMemories: build.query<
      Memory[],
      {
        date: string;
        exclude_tracker_entries: null | 'all' | Array<'macOS' | 'Windows' | 'Linux'>;
      }
    >({
      providesTags: ['timelyLegacyMemories'],
      // Simplify the caching key by only using the date. See `getTimelyLegacyMemoriesRTKQueryBase`
      // where it's nice to only need to supply `date` to retrieve the cache.
      serializeQueryArgs: ({ queryArgs }) => ({ date: queryArgs.date }),
      queryFn: async ({ date, exclude_tracker_entries }) => {
        try {
          const suggested = await api.getSuggestedEntries(date, { exclude_tracker_entries });

          // Extract entries from suggested_entries (see timeline.reducer's `fetchedSuggestedEntries` case for origin)
          const entries = suggested.reduce((prev, suggestedEntry: SuggestedEntry) => {
            prev.push(...suggestedEntry.entries);
            return prev;
          }, [] as Entry[]);

          // Return Timely's memories in TIC format
          return { data: timelyEntryToTicMemory(entries) };
        } catch (error) {
          return { error };
        }
      },
    }),
    /* Delete multiple entry/memory ids */
    deleteTimelyLegacyMemories: build.mutation<unknown, Array<number>>({
      // A bit over-kill to re-fetch everything, but this'll rarely be used
      invalidatesTags: ['timelyLegacyMemories'],
      queryFn: async ids => {
        try {
          const data = (await api.deleteEntryIds(ids)) as unknown;
          return { data };
        } catch (error) {
          return { error };
        }
      },
    }),
  }),
});
